/**
 * UTM capture for the storefront — last-touch attribution.
 *
 * On any page load with utm_* params we overwrite stored values; values expire
 * after 30 days. Stored in localStorage so it spans tabs and sessions.
 */

const STORAGE_KEY = "tripcart_utm";
const TTL_MS = 30 * 24 * 60 * 60 * 1000;

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "utm_id", "utm_referrer"];
const ALL_ATTRIBUTION_KEYS = [...UTM_KEYS, "booking_source_url"];

const safeLocalStorage = () => {
    try {
        return window.localStorage;
    } catch (_e) {
        return null;
    }
};

const parseQuery = (search) => {
    if (!search) {
        return {};
    }
    try {
        const params = new URLSearchParams(search);
        const out = {};
        for (const key of UTM_KEYS) {
            const value = params.get(key);
            if (value && value.trim() !== "") {
                out[key] = value.slice(0, 255);
            }
        }
        return out;
    } catch (_e) {
        return {};
    }
};

export const captureAndStoreUtms = () => {
    if (typeof window === "undefined") {
        return null;
    }

    const incomingUtms = parseQuery(window.location.search);

    // A fresh marketing landing carries at least one tracking param other than our
    // internal utm_referrer. Plain internal navigation carries none.
    const hasMarketingUtms = Object.keys(incomingUtms).some((key) => key !== "utm_referrer");

    // No new marketing params — keep the stored attribution as-is and don't slide
    // the TTL on every pageview.
    if (!hasMarketingUtms) {
        return readUtms();
    }

    // Last-touch: the newest marketing landing fully replaces the stored set — both
    // the UTM fields and the exact source URL point at the latest source — and resets
    // the 30-day window. Stale fields from a previous source are not carried over.
    const updatedValues = { ...incomingUtms, booking_source_url: window.location.href };

    const storage = safeLocalStorage();
    if (!storage) {
        return updatedValues;
    }

    const record = {
        values: updatedValues,
        expiresAt: Date.now() + TTL_MS,
    };

    try {
        storage.setItem(STORAGE_KEY, JSON.stringify(record));
    } catch (_e) {
        // storage quota/disabled — fall through with in-memory values
    }

    return updatedValues;
};

export const readUtms = () => {
    const storage = safeLocalStorage();
    if (!storage) {
        return {};
    }

    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) {
        return {};
    }

    let parsed;
    try {
        parsed = JSON.parse(raw);
    } catch (_e) {
        storage.removeItem(STORAGE_KEY);
        return {};
    }

    if (!parsed || typeof parsed !== "object" || !parsed.values || typeof parsed.expiresAt !== "number") {
        storage.removeItem(STORAGE_KEY);
        return {};
    }

    if (Date.now() > parsed.expiresAt) {
        storage.removeItem(STORAGE_KEY);
        return {};
    }

    return parsed.values || {};
};

export const injectUtmHiddenInputs = (formElement) => {
    if (!formElement || typeof formElement.appendChild !== "function") {
        return;
    }

    const values = readUtms();
    for (const key of ALL_ATTRIBUTION_KEYS) {
        const value = values[key];
        if (!value) {
            continue;
        }

        const existing = formElement.querySelector(`input[name="${key}"]`);
        if (existing) {
            existing.value = value;
            continue;
        }

        const input = document.createElement("input");
        input.type = "hidden";
        input.name = key;
        input.value = value;
        formElement.appendChild(input);
    }
};
